<template>
  <div class="inner-card" :style="{ background: backgroundColor }">
    <loading :active.sync="isLoading" :enforce-focus="false" loader="dots" />

    <ValidationObserver v-slot="{ handleSubmit }">
      <div class="row">
        <template v-for="(item, index) in education">
          <div class="col-md-6" :key="'educationCategory' + index">
            <div class="field">
              <ValidationProvider rules="required" v-slot="{ errors }">
                <div class="field-title">
                  <span class="mark">*</span>
                  <span :style="{ color: fontColor }">
                    學歷
                    <span class="en-text">Educational background</span>
                  </span>
                </div>
                <div class="field-value">
                  <el-select
                    class="w-100"
                    filterable
                    placeholder="請選擇"
                    v-model="item.EducationCategoryGuid"
                    @change="changeEducationDegree(item)"
                  >
                    <el-option
                      v-for="educationDegree in educationDegreeOptions"
                      :key="educationDegree.Guid"
                      :label="educationDegree.Name"
                      :value="educationDegree.Guid"
                    >
                    </el-option>
                  </el-select>
                </div>
                <span v-if="errors[0]" class="text-danger">
                  {{ errors[0] }}
                </span>
              </ValidationProvider>
            </div>
          </div>

          <div
            :class="
              item.SchoolGuid === '11111111-1111-1111-1111-111111111111'
                ? 'col-md-3'
                : 'col-md-6'
            "
            :key="'School' + index"
          >
            <div class="field">
              <ValidationProvider rules="required" v-slot="{ errors }">
                <div class="field-title">
                  <span class="mark">*</span>
                  <span :style="{ color: fontColor }">
                    學校名稱
                    <span class="en-text">School name</span>
                  </span>
                </div>
                <div class="field-value">
                  <el-select
                    class="w-100"
                    filterable
                    placeholder="請選擇"
                    v-model="item.SchoolGuid"
                    :disabled="
                      item.EducationCategoryGuid === highSchoolGuid ||
                      item.EducationCategoryGuid === secondaryGuid ||
                      item.EducationCategoryGuid === elementarySchoolGuid
                    "
                  >
                    <el-option
                      v-for="school in schoolOptions"
                      :key="school.Guid"
                      :label="school.Name"
                      :value="school.Guid"
                    >
                      <div
                        style="
                          text-overflow: ellipsis;
                          overflow: hidden;
                          max-width: calc(100vw - 135px);
                          white-space: nowrap;
                        "
                      >
                        {{ school.Name }}
                      </div>
                    </el-option>
                  </el-select>
                </div>
                <span v-if="errors[0]" class="text-danger">
                  {{ errors[0] }}
                </span>
              </ValidationProvider>
            </div>
          </div>

          <div
            v-if="item.SchoolGuid === '11111111-1111-1111-1111-111111111111'"
            class="col-md-3"
            :key="'schoolDescription' + index"
          >
            <div class="field">
              <ValidationProvider rules="required" v-slot="{ errors }">
                <div class="field-title">
                  <span class="mark">*</span>
                  <span :style="{ color: fontColor }">
                    其他學校名稱
                    <span class="en-text">School name</span>
                  </span>
                </div>
                <div class="field-value">
                  <input
                    type="text"
                    maxlength="200"
                    autocomplete="off"
                    readonly
                    @focus="$common.removeReadonly($event)"
                    v-model="item.SchoolDescription"
                  />
                </div>
                <span v-if="errors[0]" class="text-danger">
                  {{ errors[0] }}
                </span>
              </ValidationProvider>
            </div>
          </div>

          <div class="col-md-6" :key="'majorSubject' + index">
            <div class="field">
              <ValidationProvider rules="required" v-slot="{ errors }">
                <div class="field-title">
                  <span class="mark">*</span>
                  <span :style="{ color: fontColor }">
                    科系類別
                    <span class="en-text">Course name</span>
                  </span>
                </div>
                <div class="field-value">
                  <el-select
                    class="w-100"
                    filterable
                    placeholder="請選擇"
                    v-model="item.MajorGuid"
                    :disabled="
                      item.EducationCategoryGuid === highSchoolGuid ||
                      item.EducationCategoryGuid === secondaryGuid ||
                      item.EducationCategoryGuid === elementarySchoolGuid
                    "
                  >
                    <el-option
                      v-for="major in majorOptions"
                      :key="major.Guid"
                      :label="major.Name"
                      :value="major.Guid"
                    >
                      <div
                        style="
                          text-overflow: ellipsis;
                          overflow: hidden;
                          max-width: calc(100vw - 135px);
                          white-space: nowrap;
                        "
                      >
                        {{ major.Name }}
                      </div>
                    </el-option>
                  </el-select>
                </div>
                <span v-if="errors[0]" class="text-danger">
                  {{ errors[0] }}
                </span>
              </ValidationProvider>
            </div>
          </div>

          <div class="col-md-6" :key="'major' + index">
            <div class="field">
              <ValidationProvider rules="required" v-slot="{ errors }">
                <div class="field-title">
                  <span class="mark">*</span>
                  <span :style="{ color: fontColor }">
                    科系名稱
                    <span class="en-text">Course category</span>
                  </span>
                </div>
                <div class="field-value">
                  <input
                    type="text"
                    maxlength="50"
                    autocomplete="off"
                    readonly
                    @focus="$common.removeReadonly($event)"
                    v-model="item.MajorName"
                  />
                </div>
                <span v-if="errors[0]" class="text-danger">
                  {{ errors[0] }}
                </span>
              </ValidationProvider>
            </div>
          </div>

          <div class="col-md-6" :key="'isDaySchool' + index">
            <div class="field">
              <div class="field-title">
                <span :style="{ color: fontColor }">
                  日校/夜校
                  <span class="en-text">Day school/Night school</span>
                </span>
              </div>
              <div class="field-radio">
                <label>
                  <input
                    type="radio"
                    :value="true"
                    v-model="item.IsDaySchool"
                  />
                  <span>日校 (Day school)</span>
                </label>
                <label>
                  <input
                    type="radio"
                    :value="false"
                    v-model="item.IsDaySchool"
                  />
                  <span>夜校 (Night school)</span>
                </label>
              </div>
            </div>
          </div>

          <div class="col-md-6" :key="'isGraduate' + index">
            <div class="field">
              <ValidationProvider rules="required" v-slot="{ errors }">
                <div class="field-title">
                  <span class="mark">*</span>
                  <span :style="{ color: fontColor }">
                    是否畢業
                    <span class="en-text">Whether or not graduated</span>
                  </span>
                </div>
                <div class="field-radio">
                  <label>
                    <input
                      type="radio"
                      :value="true"
                      v-model="item.IsGraduate"
                    />
                    <span>是 (Yes)</span>
                  </label>
                  <label>
                    <input
                      type="radio"
                      :value="false"
                      v-model="item.IsGraduate"
                    />
                    <span>否 (No)</span>
                  </label>
                </div>
                <span v-if="errors[0]" class="text-danger">
                  {{ errors[0] }}
                </span>
              </ValidationProvider>
            </div>
          </div>

          <div class="col-md-3" :key="'educationFrom' + index">
            <div class="field">
              <ValidationProvider rules="required" v-slot="{ errors }">
                <div class="field-title">
                  <span class="mark">*</span>
                  <span :style="{ color: fontColor }">
                    入學年月
                    <span class="en-text">Enrollment period (Start)</span>
                  </span>
                </div>
                <div class="field-value">
                  <el-date-picker
                    class="w-100"
                    type="month"
                    value-format="yyyy-MM"
                    placeholder="YYYY-MM"
                    v-model="item.EducationFrom"
                  />
                </div>
                <span v-if="errors[0]" class="text-danger">
                  {{ errors[0] }}
                </span>
              </ValidationProvider>
            </div>
          </div>

          <div class="col-md-3" :key="'educationTo' + index">
            <div class="field">
              <ValidationProvider
                :rules="{
                  notBeforeStartDate: item.EducationFrom,
                  required: item.IsGraduate ? true : false,
                }"
                v-slot="{ errors }"
              >
                <div class="field-title">
                  <span :style="{ color: fontColor }">
                    <span v-if="item.IsGraduate" class="mark">*</span>
                    離校年月
                    <span class="en-text">Enrollment period (End)</span>
                  </span>
                </div>
                <div class="field-value">
                  <el-date-picker
                    class="w-100"
                    type="month"
                    value-format="yyyy-MM"
                    placeholder="yyyy-MM"
                    v-model="item.EducationTo"
                  />
                </div>
                <span v-if="errors[0]" class="text-danger">
                  {{ errors[0] }}
                </span>
              </ValidationProvider>
            </div>
          </div>

          <div class="col-md-6" :key="'button' + index">
            <button
              v-if="education.length > 1"
              type="button"
              class="del-btn"
              @click="del(index)"
            >
              {{ $t("BUTTON.RESUME_DELETE") }}
            </button>
          </div>
        </template>
      </div>

      <div class="edit-btn-wrap">
        <!-- 新增 -->
        <button type="button" class="btn-sm add-btn" @click="add">
          {{ $t("BUTTON.RESUME_ADD") }}
        </button>
        <!-- 儲存 -->
        <button
          type="button"
          class="put-btn"
          @click="handleSubmit(saveEducation)"
        >
          {{ $t("BUTTON.RESUME_SAVE") }}
        </button>
      </div>
    </ValidationObserver>
  </div>
</template>

<script>
import { mapActions } from "vuex";
export default {
  props: ["source", "nextTab", "matchId"],
  name: "AseIDLEducation",

  data() {
    return {
      highSchoolGuid: "99999999-9999-9999-9999-999999999999",
      secondaryGuid: "99999999-9999-9999-9999-999999999998",
      elementarySchoolGuid: "99999999-9999-9999-9999-999999999997",
      education: [],

      schoolOptions: [],
      majorOptions: [],
      educationDegreeOptions: [],

      backgroundColor: "#f1f1f1",
      fontColor: "#000",
    };
  },

  methods: {
    // 儲存 學歷
    saveEducation() {
      this.$api.idl
        .putEducation(this.source, {
          Details: this.education,
          UserId:
            this.source === "resume" ? this.$cookies.get("candidateId") : null,
          MatchId: this.source === "personnelInformation" ? this.matchId : null,
        })
        .then((response) => {
          if (response.status === 200) {
            this.$notify({
              title: "成功",
              message: "學歷儲存成功",
              type: "success",
              duration: 2000,
            });
            if (this.source === "resume") {
              this.$store.dispatch("GetResumeCompleteness");
            }
            this.getEducation();
            this.$emit("changeTab", this.nextTab);
          }
        });
    },

    // 取得 學歷
    getEducation() {
      const candidate =
        this.source === "resume"
          ? this.$cookies.get("candidateId")
          : this.matchId;
      this.updateLoading(true);
      this.$api.idl.getEducation(this.source, candidate).then((response) => {
        if (response.status === 200) {
          if (response.data.length === 0) {
            this.education.push({});
          } else {
            this.education = response.data;
          }
        }
        this.updateLoading(false);
      });
    },

    //學制變更
    changeEducationDegree(item) {
      if (
        item.EducationCategoryGuid === this.highSchoolGuid ||
        item.EducationCategoryGuid === this.secondaryGuid ||
        item.EducationCategoryGuid === this.elementarySchoolGuid
      ) {
        item.SchoolGuid = "11111111-1111-1111-1111-111111111111";
        item.MajorGuid = "11111111-1111-1111-1111-111111111111";
      } else {
        item.SchoolGuid = null;
        item.MajorGuid = null;
      }
    },

    // 新增
    add() {
      this.education.push({
        id: null,
      });
    },

    // 刪除
    del(index) {
      this.education.splice(index, 1);
    },

    async getData() {
      const promises = [
        this.$api.education.getSchool(),
        this.$api.education.getMajor(),
        this.$api.education.getEducationDegree(),
      ];
      const results = await Promise.allSettled(promises);
      results.forEach((data, index) => {
        switch (index) {
          case 0:
            if (data.status === "fulfilled") {
              this.schoolOptions = data.value.data.map((item) => {
                return {
                  Guid: item.Guid,
                  Name: item.Name,
                };
              });
            } else {
              console.error("取得學校選項失敗");
            }
            break;
          case 1:
            if (data.status === "fulfilled") {
              this.majorOptions = data.value.data.map((item) => {
                return {
                  Guid: item.Guid,
                  Name: item.Name,
                };
              });
            } else {
              console.error("取得科系選項失敗");
            }
            break;
          case 2:
            if (data.status === "fulfilled") {
              this.educationDegreeOptions = data.value.data.map((item) => {
                return {
                  Guid: item.Guid,
                  Name: item.Name,
                };
              });
            } else {
              console.error("取得學歷程度選項失敗");
            }
            break;
        }
      });
    },

    // loading
    ...mapActions(["updateLoading"]),
  },

  computed: {
    isLoading() {
      return this.$store.state.isLoading;
    },
  },

  created() {
    this.getData();
    this.getEducation();
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  },
};
</script>

<style lang="scss" scoped>
.field-title {
  .en-text {
    opacity: 0.6;
    font-size: 0.85em;
    margin-left: 5px;
  }
}

button {
  border: none;
  border-radius: 5px;
  padding: 5px 10px;
  font-size: 15px;
}

.del-btn {
  background: #ff3232;
  margin-top: 20px;
  color: #fff;
  margin-right: 10px;
}

.edit-btn-wrap {
  margin-top: 20px;
  button {
    background: #18c58b;
    color: #fff;
  }
  .add-btn {
    background: #fff;
    color: #000;
    margin-right: 5px;
  }
}
/deep/ .el-input.is-disabled .el-input__inner {
  background-color: #fff;
  color: #5b5b5b;
  cursor: default;
}
</style>
